import { useRef, useEffect } from "react";

/**
 * Custom hook that preserves the previous non-zero value while saving is in progress.
 * This prevents UI flickering when values temporarily reset during save operations.
 *
 * @param currentValue - The current value to track
 * @param isSaving - Flag indicating if a save operation is in progress
 * @returns The effective value (previous if saving and current is 0, otherwise current)
 */
export function usePreviousValueWhileSaving(
  currentValue: number,
  isSaving: boolean
): number {
  const previousValueRef = useRef<number>(0);

  useEffect(() => {
    // Store the current value when not saving and it's greater than 0
    if (!isSaving && currentValue > 0) {
      previousValueRef.current = currentValue;
    }
  }, [isSaving, currentValue]);

  // Return previous value if saving and current value is 0, otherwise return current value
  return isSaving && currentValue === 0
    ? previousValueRef.current
    : currentValue;
}
